import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Building2, MapPin, Phone, Settings } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { getApiUrl } from '../utils/api';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';

interface CompanyFormData {
  name: string;
  legalName: string;
  siret: string;
  vatNumber: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  phone: string;
  email: string;
  website: string;
  timezone: string;
  defaultSlotSizeMinutes: number;
  isActive: boolean;
}

const timezones = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Madrid',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney'
];

const slotSizes = [5, 10, 15, 30, 45, 60];

export function CompanyCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    legalName: '',
    siret: '',
    vatNumber: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'France'
    },
    phone: '',
    email: '',
    website: '',
    timezone: 'Europe/Paris',
    defaultSlotSizeMinutes: 15,
    isActive: true
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('companyForm.validation.nameRequired');
    }

    if (!formData.legalName.trim()) {
      newErrors.legalName = t('companyForm.validation.legalNameRequired');
    }

    if (!phoneDisplay.trim()) {
      newErrors.phone = t('companyForm.validation.phoneRequired');
    } else if (!phoneIsValid || !phoneE164) {
      newErrors.phone = t('quickCreate.validation.phoneInvalid');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('companyForm.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('companyForm.validation.emailInvalid');
    }

    if (!formData.address.street.trim()) {
      newErrors['address.street'] = t('companyForm.validation.streetRequired');
    }

    if (!formData.address.city.trim()) {
      newErrors['address.city'] = t('companyForm.validation.cityRequired');
    }

    if (!formData.address.postalCode.trim()) {
      newErrors['address.postalCode'] = t('companyForm.validation.postalCodeRequired');
    }

    if (!formData.address.country.trim()) {
      newErrors['address.country'] = t('companyForm.validation.countryRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !phoneE164) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl('/api/admin/companies'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, phone: phoneE164 }),
      });

      const data = await response.json();
      
      if (data.success) {
        navigate('/companies');
      } else {
        setError(data.message || 'Échec de la création de l\'entreprise');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Create company error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/companies');
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleCancel}
          className="inline-flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('common.cancel')}</span>
        </button>
        
        <h1 className="text-3xl font-display font-bold text-textPrimary mb-2 flex items-center space-x-2">
          <span>{t('companyForm.createTitle')}</span>
          <HelpIcon 
            docPath="03-entreprises#03-3-creation" 
            tooltip="Documentation : Création d'une entreprise"
          />
        </h1>
        <p className="text-textSecondary">
          {t('companyForm.createSubtitle')}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-primary-600" />
            {t('companyForm.basicInfo')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.name')} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`input-field ${errors.name ? 'border-red-300' : ''}`}
                placeholder={t('companyForm.namePlaceholder')}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.legalName')} *
              </label>
              <input
                type="text"
                value={formData.legalName}
                onChange={(e) => handleInputChange('legalName', e.target.value)}
                className={`input-field ${errors.legalName ? 'border-red-300' : ''}`}
                placeholder={t('companyForm.legalNamePlaceholder')}
              />
              {errors.legalName && <p className="mt-1 text-sm text-red-600">{errors.legalName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.siret')}
              </label>
              <input
                type="text"
                value={formData.siret}
                onChange={(e) => handleInputChange('siret', e.target.value)}
                className="input-field"
                placeholder={t('companyForm.siretPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.vatNumber')}
              </label>
              <input
                type="text"
                value={formData.vatNumber}
                onChange={(e) => handleInputChange('vatNumber', e.target.value)}
                className="input-field"
                placeholder={t('companyForm.vatNumberPlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-primary-600" />
            {t('companyForm.address')}
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.street')} *
              </label>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => handleInputChange('address.street', e.target.value)}
                className={`input-field ${errors['address.street'] ? 'border-red-300' : ''}`}
                placeholder={t('companyForm.streetPlaceholder')}
              />
              {errors['address.street'] && <p className="mt-1 text-sm text-red-600">{errors['address.street']}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('companyForm.city')} *
                </label>
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  className={`input-field ${errors['address.city'] ? 'border-red-300' : ''}`}
                  placeholder={t('companyForm.cityPlaceholder')}
                />
                {errors['address.city'] && <p className="mt-1 text-sm text-red-600">{errors['address.city']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('companyForm.postalCode')} *
                </label>
                <input
                  type="text"
                  value={formData.address.postalCode}
                  onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                  className={`input-field ${errors['address.postalCode'] ? 'border-red-300' : ''}`}
                  placeholder={t('companyForm.postalCodePlaceholder')}
                />
                {errors['address.postalCode'] && <p className="mt-1 text-sm text-red-600">{errors['address.postalCode']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('companyForm.country')} *
                </label>
                <input
                  type="text"
                  value={formData.address.country}
                  onChange={(e) => handleInputChange('address.country', e.target.value)}
                  className={`input-field ${errors['address.country'] ? 'border-red-300' : ''}`}
                  placeholder={t('companyForm.countryPlaceholder')}
                />
                {errors['address.country'] && <p className="mt-1 text-sm text-red-600">{errors['address.country']}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Phone className="w-5 h-5 mr-2 text-primary-600" />
            {t('companyForm.contact')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.phone')} *
              </label>
              <InternationalPhoneInput
                value={phoneDisplay}
                country={phoneCountry}
                required
                onChange={(result) => {
                  setPhoneDisplay(result.displayValue);
                  setPhoneCountry(result.country);
                  setPhoneE164(result.e164Value);
                  setPhoneIsValid(result.isValid);
                  setFormData((prev) => ({ ...prev, phone: result.displayValue }));
                  if (result.isValid && errors.phone) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.phone;
                      return next;
                    });
                  }
                }}
                selectClassName="input-field"
                inputClassName={`input-field ${errors.phone ? 'border-red-300' : ''}`}
                placeholder={t('companyForm.phonePlaceholder')}
                error={errors.phone}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.email')} *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`input-field ${errors.email ? 'border-red-300' : ''}`}
                placeholder={t('companyForm.emailPlaceholder')}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.website')}
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                className="input-field"
                placeholder={t('companyForm.websitePlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* Settings Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Settings className="w-5 h-5 mr-2 text-primary-600" />
            {t('companyForm.settings')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.timezone')}
              </label>
              <select
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                className="input-field"
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyForm.defaultSlotSize')}
              </label>
              <select
                value={formData.defaultSlotSizeMinutes}
                onChange={(e) => handleInputChange('defaultSlotSizeMinutes', parseInt(e.target.value))}
                className="input-field"
              >
                {slotSizes.map(size => (
                  <option key={size} value={size}>{size} minutes</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                {t('companyForm.isActive')}
              </span>
            </label>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('companyForm.creating')}</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>{t('companyForm.create')}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}


